/**
 * Client wrapper for `/api/workspace/providers` and `/api/workspace/models`.
 * Handles connect / disconnect of AI providers at the workspace level and
 * add / remove of user-entered custom models.
 */

import type { ReasoningEffort } from "@taqat/core/types";
import { api } from "@/utils/api-client";
import type { ModelCapabilities } from "@taqat/core/types";

export type ProviderEnum =
  | "OPENAI"
  | "ANTHROPIC"
  | "GROQ"
  | "OPEN_ROUTER"
  | "DEEPSEEK"
  | "CODEX"
  | "ZAI"
  | "OLLAMA"
  | "LM_STUDIO"
  | "CLAUDE_CODE"
  | "CUSTOM";

export interface ProviderConnection {
  id: string;
  provider: ProviderEnum;
  isConnected: boolean;
  usesSystemKey: boolean;
  hasApiKey: boolean;
  baseUrl: string | null;
  connectedAt: string | null;
}

export interface WorkspaceCustomModel {
  id: string;
  provider: ProviderEnum;
  providerModelId: string;
  displayName: string;
  costInputPcuPer1k: string;
  costOutputPcuPer1k: string;
  hasReasoning: boolean;
  /**
   * Reasoning rungs this model can actually be asked for, ascending.
   * Computed server-side (model's declared ladder intersected with what our
   * SDK version can transmit). Fewer than two entries means there is nothing
   * to choose and no control should render.
   */
  reasoningOptions?: ReasoningEffort[];
  providerConnected: boolean;
  /**
   * Model capability contract. Drives chat-UI affordance gating
   * (file/image attach button) and surfaces the per-model token caps.
   */
  capabilities: ModelCapabilities;
  createdAt: string;
}

export interface ConnectProviderInput {
  apiKey?: string;
  baseUrl?: string;
}

export interface AddCustomModelInput {
  provider: ProviderEnum;
  providerModelId: string;
  displayName: string;
  costInputPcuPer1k?: number;
  costOutputPcuPer1k?: number;
  hasReasoning?: boolean;
  capabilities?: ModelCapabilities;
}

/**
 * Update payload — provider and providerModelId are intentionally absent.
 * Switching either invalidates the row's identity and breaks credit
 * attribution for chats already using the model. Delete + re-add to change.
 */
export interface UpdateCustomModelInput {
  displayName?: string;
  costInputPcuPer1k?: number;
  costOutputPcuPer1k?: number;
  hasReasoning?: boolean;
  capabilities?: ModelCapabilities;
}

/**
 * A selectable model returned by the provider's live catalog. Only `id` and
 * `displayName` are guaranteed; the rest is best-effort metadata used to
 * auto-fill the Add-model form (present for e.g. OpenRouter, absent for
 * providers whose list endpoint only returns ids).
 */
export interface ProviderModelOption {
  id: string;
  displayName: string;
  contextLength?: number;
  pricing?: { inputPerMillion?: number; outputPerMillion?: number };
  capabilities?: Partial<ModelCapabilities>;
  hasReasoning?: boolean;
}

/** Envelope returned by `successResponse` / `createdResponse` on the server. */
interface Envelope<T> {
  success: boolean;
  data: T;
}

export const workspaceProviderService = {
  async listProviders(): Promise<ProviderConnection[]> {
    const res = (await api.get("/workspace/providers", {
      requiresAuth: true,
    })) as Envelope<{ providers: ProviderConnection[] }>;
    return res.data.providers;
  },

  async connect(
    provider: ProviderEnum,
    input: ConnectProviderInput,
  ): Promise<ProviderConnection> {
    const res = (await api.post(`/workspace/providers/${provider}/connect`, input, {
      requiresAuth: true,
    })) as Envelope<{ provider: ProviderConnection }>;
    return res.data.provider;
  },

  async disconnect(provider: ProviderEnum): Promise<ProviderConnection> {
    const res = (await api.delete(`/workspace/providers/${provider}`, {
      requiresAuth: true,
    })) as Envelope<{ provider: ProviderConnection }>;
    return res.data.provider;
  },

  /**
   * Lists the models a thread-owning CLI provider (Codex, Claude Code) can run,
   * queried live from the provider. Returns [] for non-thread-owning providers.
   */
  async listAvailableModels(
    provider: ProviderEnum,
  ): Promise<ProviderModelOption[]> {
    const res = (await api.get(
      `/workspace/providers/${provider}/available-models`,
      { requiresAuth: true },
    )) as Envelope<{ models: ProviderModelOption[] }>;
    return res.data.models;
  },

  /**
   * Measures what an OpenAI-compatible endpoint does with `reasoning_effort`.
   *
   * Costs up to three short completions against the workspace's own
   * credentials. Fired automatically when a model is chosen (debounced, and
   * skipped for an unchanged model that already has a stored capability), so
   * the caller must keep it off any path that runs per keystroke. Admin-only
   * and rate-limited server-side.
   */
  async detectReasoning(
    provider: ProviderEnum,
    providerModelId: string,
  ): Promise<{ summary: string; capability?: ModelCapabilities["reasoning"] }> {
    const response = (await api.post(
      `/workspace/providers/${provider}/detect-reasoning`,
      { providerModelId },
      { requiresAuth: true },
    )) as { data: { summary: string; capability?: ModelCapabilities["reasoning"] } }
    return response.data
  },

  async listCustomModels(): Promise<WorkspaceCustomModel[]> {
    const res = (await api.get("/workspace/models", {
      requiresAuth: true,
    })) as Envelope<{ models: WorkspaceCustomModel[] }>;
    return res.data.models;
  },

  async addCustomModel(input: AddCustomModelInput): Promise<WorkspaceCustomModel> {
    const res = (await api.post("/workspace/models", input, {
      requiresAuth: true,
    })) as Envelope<{ model: WorkspaceCustomModel }>;
    return res.data.model;
  },

  async updateCustomModel(
    id: string,
    input: UpdateCustomModelInput,
  ): Promise<WorkspaceCustomModel> {
    const res = (await api.patch(`/workspace/models/${id}`, input, {
      requiresAuth: true,
    })) as Envelope<{ model: WorkspaceCustomModel }>;
    return res.data.model;
  },

  async deleteCustomModel(id: string): Promise<void> {
    // Suppress the global error toast — the caller (AI Models settings) catches
    // the error and shows a single, specific toast (e.g. the in-use 409 message).
    await api.delete(`/workspace/models/${id}`, {
      requiresAuth: true,
      showErrorToast: false,
    });
  },
};
